import { z } from 'zod';
/**
 * Politica de senha alinhada ao NIST SP 800-63B: comprimento manda, composicao
 * forcada (1 maiuscula, 1 simbolo...) nao. O que barramos de fato e o que
 * quebra na pratica: senha curta, senha obvia, e senha derivada do proprio
 * e-mail ou nome.
 *
 * Limite superior de 128: argon2id sobre uma senha de 1 MB e um vetor de DoS.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
/**
 * Amostra das senhas mais vazadas, incluindo as brasileiras. Nao substitui uma
 * checagem contra o HIBP, que fica para a Fase 10 (k-anonymity, sem enviar a
 * senha) — este e o piso barato que pega o caso comum.
 */
const COMMON_PASSWORDS = new Set([
    '123456',
    '123456789',
    '12345678',
    '1234567890',
    'password',
    'senha123',
    'senha1234',
    'qwerty123',
    'admin123',
    'brasil123',
    'mudar123',
    'trocar123',
    'abc123456',
    'iloveyou',
    'primeiro123',
    'flamengo123',
    'corinthians',
    'palmeiras123',
    'saopaulo123',
    'gremio123',
    'internacional',
    'santos123',
    'cruzeiro123',
    'vasco12345',
    'brasil2026',
    'password123',
    'p@ssw0rd123',
    'letmein123',
    'welcome123',
    'monkey12345',
    'dragon12345',
    'familia123',
    'teamo123456',
    'deusefiel123',
    'jesuscristo1',
]);
/** Retorna a lista de problemas. Vazia significa senha aceita. */
export function passwordIssues(password, context = {}) {
    const issues = [];
    if (password.length < PASSWORD_MIN_LENGTH) {
        issues.push(`A senha precisa de ao menos ${PASSWORD_MIN_LENGTH} caracteres.`);
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
        issues.push(`A senha nao pode passar de ${PASSWORD_MAX_LENGTH} caracteres.`);
    }
    if (password.trim().length === 0) {
        issues.push('A senha nao pode ser apenas espacos.');
    }
    const normalized = password.toLowerCase().replace(/\s+/g, '');
    if (COMMON_PASSWORDS.has(normalized)) {
        issues.push('Esta senha aparece em listas de senhas vazadas. Escolha outra.');
    }
    if (/^(.)\1+$/.test(password)) {
        issues.push('A senha nao pode ser um unico caractere repetido.');
    }
    if (isSequential(normalized)) {
        issues.push('A senha nao pode ser uma sequencia como 12345678 ou abcdefgh.');
    }
    // Senha so de digitos e, na pratica, data, telefone ou CPF: cerca de 40 bits
    // para 12 caracteres, contra ~78 de uma senha alfanumerica do mesmo tamanho.
    // Nao e uma regra de composicao a moda antiga — e o piso de entropia.
    if (/^\d+$/.test(password)) {
        issues.push('A senha nao pode ser formada apenas por numeros.');
    }
    // "abababababab" tem 12 caracteres e duas letras de variedade.
    if (new Set(password).size < 5) {
        issues.push('A senha precisa de pelo menos 5 caracteres diferentes.');
    }
    const localPart = context.email?.split('@')[0]?.toLowerCase();
    if (localPart && localPart.length >= 3 && normalized.includes(localPart)) {
        issues.push('A senha nao pode conter o seu e-mail.');
    }
    if (context.name) {
        const firstName = context.name.trim().split(/\s+/)[0]?.toLowerCase();
        if (firstName && firstName.length >= 3 && normalized.includes(firstName)) {
            issues.push('A senha nao pode conter o seu nome.');
        }
    }
    return issues;
}
function isSequential(value) {
    if (value.length < 6)
        return false;
    let ascending = true;
    let descending = true;
    for (let i = 1; i < value.length; i += 1) {
        const delta = value.charCodeAt(i) - value.charCodeAt(i - 1);
        if (delta !== 1)
            ascending = false;
        if (delta !== -1)
            descending = false;
    }
    return ascending || descending;
}
/**
 * Schema sem contexto — usado onde e-mail e nome nao estao disponiveis.
 * Prefira `passwordSchemaWithContext` no cadastro.
 */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH)
    .max(PASSWORD_MAX_LENGTH)
    .superRefine((value, ctx) => {
    for (const issue of passwordIssues(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
    }
});
//# sourceMappingURL=password.js.map